import { api } from '@/lib/api';
import type { Role } from './roles';
import type { Me, MfaChallenge, MfaSetup, TokenResponse } from './types';

// Auth endpoints never trigger the client's own refresh-and-retry.
const NO_RETRY = { skipAuthRetry: true } as const;

export function isMfaChallenge(
  res: TokenResponse | MfaChallenge,
): res is MfaChallenge {
  return 'mfaRequired' in res && res.mfaRequired === true;
}

export const authApi = {
  login: async (email: string, password: string, role?: Role) =>
    (
      await api.post<TokenResponse | MfaChallenge>(
        '/auth/login',
        { email, password, ...(role ? { role } : {}) },
        NO_RETRY,
      )
    ).data,
  mfaSetup: async (mfaToken: string) =>
    (await api.post<MfaSetup>('/auth/mfa/setup', { mfaToken }, NO_RETRY)).data,
  mfaVerify: async (
    mfaToken: string,
    proof: { code: string } | { recoveryCode: string },
  ) =>
    (
      await api.post<TokenResponse>(
        '/auth/mfa/verify',
        { mfaToken, ...proof },
        NO_RETRY,
      )
    ).data,
  refresh: async () =>
    (await api.post<TokenResponse>('/auth/refresh', undefined, NO_RETRY)).data,
  logout: async () => {
    await api.post<null>('/auth/logout', undefined, NO_RETRY);
  },
  me: async () => (await api.get<Me>('/auth/me', { cache: 'no-store' })).data,
  selectRole: async (role: Role) =>
    (await api.post<Me>('/auth/active-role', { role })).data,
  setPin: async (currentPassword: string, pin: string) =>
    (await api.post<Me>('/auth/pin', { currentPassword, pin })).data,
  lock: async () => {
    await api.post<null>('/auth/lock');
  },
  unlock: async (pin: string) => {
    await api.post<null>('/auth/unlock', { pin }, NO_RETRY);
  },
  preferences: async (prefs: { largerText?: boolean }) =>
    (await api.post<Me>('/auth/preferences', prefs)).data,
  mfaEnrol: async () => (await api.post<MfaSetup>('/auth/mfa/enrol')).data,
  mfaEnable: async (code: string) =>
    (await api.post<{ recoveryCodes: string[] }>('/auth/mfa/enable', { code }))
      .data,
  mfaRecoveryCodes: async (code: string) =>
    (
      await api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', {
        code,
      })
    ).data,
  mfaDisable: async (code: string) =>
    (await api.post<Me>('/auth/mfa/disable', { code })).data,
};
